/* eslint-disable */

import { LS_MAINTENANCE_MESSAGE_KEY } from '@/components/app/app-header-notification/app-header-notification';
import env from '@/env';
import ls from '@/helpers/localStorage';
import ss from '@/helpers/sessionStorage';
import api from '@/services/api';
import { normalizeTheme, themeStorageKey } from '@/utils/theme';

const LAYOUT_MOBILE = 'mobile';
const LAYOUT_DESKTOP = 'desktop';
const LAYOUT_SWITCH_BREAKPOINT = 910;

function getDefaultLayout() {
  const width =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.getElementsByTagName('body')[0].clientWidth;
  return width >= LAYOUT_SWITCH_BREAKPOINT ? LAYOUT_DESKTOP : LAYOUT_MOBILE;
}

export const SETTING_SHOW_ODDS = 'settings-odds-key';
export const SETTING_COLOR_PALETTE_KEY = 'settings-color-palette-key';
export const SETTING_MOBILE_LAYOUT_KEY = 'settings-mobile-layout-key';
export const SETTING_ORDERBOOK_COLLAPSED_KEY = 'settings-orderbook-collapsed-key';
export const SETTING_GUEST_COLOR_PALETTE_DIRTY_KEY = 'guest-setting-color-palette-dirty';
export const SETTING_GUEST_PRICE_DISPLAY_DIRTY_KEY = 'guest-setting-price-display-dirty';
export const SETTING_GUEST_MOBILE_LAYOUT_DIRTY_KEY = 'guest-setting-mobile-layout-dirty';
export const SETTING_DIRECTION_BUTTON_VERSION_KEY = 'settings-direction-button-version-key';
export const SETTING_GUEST_DIRECTION_BUTTON_VERSION_DIRTY_KEY =
  'guest-setting-direction-button-version-dirty';
export const SETTING_GUEST_COLOR_THEME_DIRTY_KEY = 'guest-setting-color-theme-dirty';

export const APP_COLOR_PALETTE = {
  BLUE_GREY: 2,
  GREEN_RED: 1,
};

export const APP_MOBILE_LAYOUT = {
  STANDARD: 1,
  SIMPLIFIED: 2,
  CONDENSED: 3,
};

export const APP_PRICE_DISPLAY = {
  TRADE_PRICES: 1,
  ODDS: 2, // settingShowOdds true
};

export const APP_MOBILE_ORDERBOOK_DISPLAY = {
  COLLAPSED: 0, // settingOrderbookCollapsed true
  EXPANDED: 1,
};

export const APP_DIRECTION_BUTTON_VERSION = {
  V1: 1,
  V2: 2,
};

export const APP_COLOR_THEME = {
  LIGHT: 0,
  DARK: 1,
};

export function themeToSettingValue(theme) {
  return theme === 'dark' ? APP_COLOR_THEME.DARK : APP_COLOR_THEME.LIGHT;
}

export function settingValueToTheme(value) {
  return Number.parseInt(value, 10) === APP_COLOR_THEME.DARK ? 'dark' : 'light';
}

export const UX_CATEGORY = {
  UXSettingBuyColorScheme: 'UXSettingBuyColorScheme',
  UXSettingMobileContractLayout: 'UXSettingMobileContractLayout',
  UXSettingPriceDisplay: 'UXSettingPriceDisplay',
  UXSettingIsOBCollapsedOnOS: 'UXSettingIsOBCollapsedOnOS',
  UXSettingAnnouncementCurState: 'UXSettingAnnouncementCurState',
  UXSettingDirectionButtonVersion: 'UXSettingDirectionButtonVersion',
  UXSettingColorTheme: 'UXSettingColorTheme',
};

export default {
  namespaced: true,
  state: {
    previousRoute: null,
    initialVersion: null,
    serverVersion: null,
    pendingServerVersion: null,
    skipAttachToHomeNotification: ls.get('skipAttachToHomeNotification', false),
    skipBanner: ls.get('skipBanner', false),
    hasServerErrorOccurred: false,
    layout: getDefaultLayout(),
    isTaxNotificationMandatory: false,
    hiddenMaintenanceMessage: ls.get(LS_MAINTENANCE_MESSAGE_KEY),
    maintenance: {
      isMaintenanceEnabled: false,
      isTradingEnabled: true,
      maintenanceMessage: '',
      suspendedMessage: '',
      systemMessage: '',
    },
    mobileMenuOpened: false,
    isTabActive: !document.hidden,
    liveUpdatesDisabled: false,
    firebaseInitialized: false,
    firebaseGeneration: 0,
    signalrInitialized: false,
    systemEmails: {
      support: env.EMAIL_SUPPORT,
      press: env.EMAIL_PRESS,
      markets: env.EMAIL_MARKETS,
    },
    newLoaderCounter: 0,
    settingShowOdds: ls.get(SETTING_SHOW_ODDS, true),
    settingOrderbookCollapsed: ls.get(SETTING_ORDERBOOK_COLLAPSED_KEY, false),
    settingColorPalette: ls.get(SETTING_COLOR_PALETTE_KEY, APP_COLOR_PALETTE.GREEN_RED),
    settingMobileLayout: ls.get(SETTING_MOBILE_LAYOUT_KEY, APP_MOBILE_LAYOUT.SIMPLIFIED),
    settingDirectionButtonVersion: ls.get(
      SETTING_DIRECTION_BUTTON_VERSION_KEY,
      APP_DIRECTION_BUTTON_VERSION.V1,
    ),
    guestSettingColorPaletteDirty: ss.get(SETTING_GUEST_COLOR_PALETTE_DIRTY_KEY, false),
    guestSettingPriceDisplayDirty: ss.get(SETTING_GUEST_PRICE_DISPLAY_DIRTY_KEY, false),
    guestSettingMobileLayoutDirty: ss.get(SETTING_GUEST_MOBILE_LAYOUT_DIRTY_KEY, false),
    guestSettingDirectionButtonVersionDirty: ss.get(
      SETTING_GUEST_DIRECTION_BUTTON_VERSION_DIRTY_KEY,
      false,
    ),
    guestSettingColorThemeDirty: ss.get(SETTING_GUEST_COLOR_THEME_DIRTY_KEY, false),
    isPinboardSingleModeVisible: false,
    isPinboardDualModeVisible: false,
    sidebarVisible: ls.get('pinboard-sidebar-visible', true),
    settingTheme: 'light',
    resolvedTheme: 'light',
  },
  mutations: {
    setLiveUpdatesDisabled(state, flag) {
      state.liveUpdatesDisabled = flag;
    },
    setIsTabActive(state, data) {
      state.isTabActive = data;
    },
    setPreviousRoute(state, data) {
      state.previousRoute = data;
    },
    setInitialVersion(state, version) {
      state.initialVersion = version;
    },
    setServerVersion(state, version) {
      state.serverVersion = version;
      state.pendingServerVersion = null;
    },
    setPendingServerVersion(state, version) {
      state.pendingServerVersion = version;
    },
    setLayout(state, layout) {
      state.layout = layout;
    },
    setServerError(state, hasOccurred) {
      state.hasServerErrorOccurred = hasOccurred;
    },
    setFirebaseInitialized(state, flag) {
      state.firebaseInitialized = flag;
      if (flag) state.firebaseGeneration++;
    },
    setSignalrInitialized(state, flag) {
      state.signalrInitialized = flag;
    },
    setMaintenance(state, data) {
      state.maintenance = data;
    },
    setMandatoryTaxNotification(state, flag) {
      state.isTaxNotificationMandatory = flag;
    },
    dontShowAttachToHomeNotification(state) {
      const expiresAt = 31536000; // 1 year
      ls.set('skipAttachToHomeNotification', true, expiresAt);
      state.skipAttachToHomeNotification = true;
    },
    dontShowBanner(state) {
      state.skipBanner = true;
    },
    toggleMobileMenu(state) {
      state.mobileMenuOpened = !state.mobileMenuOpened;
    },
    closeMobileMenu(state) {
      state.mobileMenuOpened = false;
    },
    setHiddenMaintenanceMessage(state, value) {
      state.hiddenMaintenanceMessage = value;
    },
    incrNewLoaderCounter(state) {
      state.newLoaderCounter = state.newLoaderCounter + 1;
    },
    decrNewLoaderCounter(state) {
      state.newLoaderCounter = state.newLoaderCounter - 1;
      if (state.newLoaderCounter < 0) {
        state.newLoaderCounter = 0;
      }
    },
    resetLoaderCounter(state) {
      state.newLoaderCounter = 0;
    },
    setSettingShowOdds(state, flag) {
      ls.set(SETTING_SHOW_ODDS, flag);
      state.settingShowOdds = flag;
    },
    setSettingOrderbookCollapsed(state, flag) {
      ls.set(SETTING_ORDERBOOK_COLLAPSED_KEY, flag);
      state.settingOrderbookCollapsed = flag;
    },
    setSettingColorPalette(state, value) {
      ls.set(SETTING_COLOR_PALETTE_KEY, value);
      state.settingColorPalette = value;
    },
    setSettingMobileLayout(state, value) {
      ls.set(SETTING_MOBILE_LAYOUT_KEY, value);
      state.settingMobileLayout = value;
    },
    setSettingDirectionButtonVersion(state, value) {
      ls.set(SETTING_DIRECTION_BUTTON_VERSION_KEY, value);
      state.settingDirectionButtonVersion = value;
    },
    setGuestSettingColorPaletteDirty(state, value) {
      if (value) {
        ss.set(SETTING_GUEST_COLOR_PALETTE_DIRTY_KEY, true);
      } else {
        ss.del(SETTING_GUEST_COLOR_PALETTE_DIRTY_KEY);
      }
      state.guestSettingColorPaletteDirty = value;
    },
    setGuestSettingPriceDisplayDirty(state, value) {
      if (value) {
        ss.set(SETTING_GUEST_PRICE_DISPLAY_DIRTY_KEY, true);
      } else {
        ss.del(SETTING_GUEST_PRICE_DISPLAY_DIRTY_KEY);
      }
      state.guestSettingPriceDisplayDirty = value;
    },
    setGuestSettingMobileLayoutDirty(state, value) {
      if (value) {
        ss.set(SETTING_GUEST_MOBILE_LAYOUT_DIRTY_KEY, true);
      } else {
        ss.del(SETTING_GUEST_MOBILE_LAYOUT_DIRTY_KEY);
      }
      state.guestSettingMobileLayoutDirty = value;
    },
    setGuestSettingDirectionButtonVersionDirty(state, value) {
      if (value) {
        ss.set(SETTING_GUEST_DIRECTION_BUTTON_VERSION_DIRTY_KEY, true);
      } else {
        ss.del(SETTING_GUEST_DIRECTION_BUTTON_VERSION_DIRTY_KEY);
      }
      state.guestSettingDirectionButtonVersionDirty = value;
    },
    setGuestSettingColorThemeDirty(state, value) {
      if (value) {
        ss.set(SETTING_GUEST_COLOR_THEME_DIRTY_KEY, true);
      } else {
        ss.del(SETTING_GUEST_COLOR_THEME_DIRTY_KEY);
      }
      state.guestSettingColorThemeDirty = value;
    },
    setPinboardModeVisible(state, { single, dual }) {
      state.isPinboardSingleModeVisible = single;
      state.isPinboardDualModeVisible = dual;
    },
    setSidebarVisible(state, flag) {
      state.sidebarVisible = flag;
      ls.set('pinboard-sidebar-visible', flag);
    },
    setThemePreference(state, value) {
      state.settingTheme = normalizeTheme(value);
    },
    setResolvedTheme(state, value) {
      state.resolvedTheme = value === 'dark' ? 'dark' : 'light';
    },
  },
  getters: {
    isLayoutMobile: (state) => state.layout === LAYOUT_MOBILE,
    isLayoutDesktop: (state) => state.layout === LAYOUT_DESKTOP,
    isShutdownModeOn: (state) => !!env.SHUTDOWN_ENABLED,
    isPinningEnabled: (state) => !!env.PINNING_ENABLED,
    isInsightsEnabled: (state) => !!env.INSIGHTS_ENABLED,
    isDepositPromoInputBoxEnabled: (state) => !!env.DEPOSIT_PROMO_INPUT_BOX_ENABLED,
    leaderboardsEnabled: (state) => !!env.LEADERBOARDS_ENABLED,
    collegeAffiliationEnabled: (state) => !!env.COLLEGE_AFFILIATION_ENABLED,
    hasMaintenanceMessage: (state) => {
      if (state.maintenance.maintenanceMessage) return true;
      const maintenanceMessage =
        state.maintenance.suspendedMessage || state.maintenance.systemMessage;
      // check if message was hidden by user;
      return maintenanceMessage && maintenanceMessage !== state.hiddenMaintenanceMessage;
    },
    hasMaintenance: (state) =>
      state.maintenance.maintenanceMessage || state.maintenance.suspendedMessage,
    hasMaintenanceSuspended: (state) => !!state.maintenance.suspendedMessage,
    hasBanner: (state, getters, rootState, rootGetters) =>
      env.BANNER_ENABLED && !state.skipBanner && rootGetters['user/isLoggedIn'],
    isAfterHours: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return false;
      // }
      // return parseInt(state.maintenance.siteState, 10) === 2;
      return false;
    },
    isRegularMaintenance: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return false;
      // }
      // return parseInt(state.maintenance.siteState, 10) === 3;
      return false;
    },
    isExtendedMaintenance: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return false;
      // }
      // return parseInt(state.maintenance.siteState, 10) === 4;
      return false;
    },
    isFullMaintenance: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return false;
      // }
      // return parseInt(state.maintenance.siteState, 10) === 5;
      return false;
    },
    isTradingEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    isCancelOrdersEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1, 2, 3].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    isDepositEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1, 2].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    isWithdrawEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1, 2].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    isCommentEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1, 2, 3, 4].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    isLoginEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1, 2, 3, 4].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    isSignUpEnabled: (state) => {
      // if (state.maintenance.siteState === null) {
      //   return true;
      // }
      // return [1, 2, 3, 4].includes(parseInt(state.maintenance.siteState, 10));
      return true;
    },
    newLoaderIsLoading: (state) => state.newLoaderCounter > 0,
    currentPriceSymbol: (state) => (state.settingShowOdds ? '%' : '&cent;'),
    isPinboardSingleModeVisible: (state) => state.isPinboardSingleModeVisible,
    isPinboardDualModeVisible: (state) => state.isPinboardDualModeVisible,
    sidebarVisible: (state) => state.sidebarVisible,
    isDirectionButtonV2: (state) =>
      state.settingDirectionButtonVersion === APP_DIRECTION_BUTTON_VERSION.V2,
  },
  actions: {
    setHiddenMaintenanceMessage({ commit }, value) {
      commit('setHiddenMaintenanceMessage', value);
    },
    setInitialVersion({ commit }, data) {
      commit('setInitialVersion', data);
    },
    setServerVersion({ commit }, data) {
      commit('setServerVersion', data);
    },
    // Multiple app-server instances behind the load balancer can report
    // different x-version headers for a few requests during a rolling
    // deploy. Only treat a new version as authoritative once it's been seen
    // twice, so a single stray response doesn't falsely trigger a reload.
    confirmServerVersion({ commit, state }, version) {
      if (version === state.serverVersion) {
        if (state.pendingServerVersion !== null) commit('setPendingServerVersion', null);
        return;
      }
      if (version === state.pendingServerVersion) {
        commit('setServerVersion', version);
      } else {
        commit('setPendingServerVersion', version);
      }
    },
    setLayoutWidth({ commit }, width) {
      if (width >= LAYOUT_SWITCH_BREAKPOINT) {
        commit('setLayout', LAYOUT_DESKTOP);
      } else {
        commit('setLayout', LAYOUT_MOBILE);
      }
    },
    setServerError({ commit }, hasOccurred) {
      commit('setServerError', hasOccurred);
    },
    setMaintenance({ commit }, data) {
      commit('setMaintenance', data);
    },
    setMandatoryTaxNotification({ commit }, flag) {
      commit('setMandatoryTaxNotification', flag);
    },
    dontShowAttachToHomeNotification({ commit }) {
      commit('dontShowAttachToHomeNotification');
    },
    dontShowBanner({ commit }) {
      commit('dontShowBanner');
    },
    setPreviousRoute({ commit }, data) {
      commit('setPreviousRoute', data);
    },
    toggleMobileMenu({ commit }) {
      commit('toggleMobileMenu');
    },
    closeMobileMenu({ commit }) {
      commit('closeMobileMenu');
    },
    setIsTabActive({ commit }, flag) {
      commit('setIsTabActive', flag);
    },
    setLiveUpdatesDisabled({ commit }, flag) {
      commit('setLiveUpdatesDisabled', flag);
    },
    setFirebaseInitialized({ commit }, flag) {
      commit('setFirebaseInitialized', flag);
    },
    setSignalrInitialized({ commit }, flag) {
      commit('setSignalrInitialized', flag);
    },
    incrNewLoaderCounter({ commit }) {
      commit('incrNewLoaderCounter');
    },
    decrNewLoaderCounter({ commit }) {
      commit('decrNewLoaderCounter');
    },
    resetLoaderCounter({ commit }) {
      commit('resetLoaderCounter');
    },
    setSettingShowOdds({ commit, rootGetters }, flag) {
      commit('setSettingShowOdds', flag);
      if (rootGetters['user/isLoggedIn']) {
        commit('setGuestSettingPriceDisplayDirty', false);
        return api.profile.putSettings(
          'ux',
          UX_CATEGORY.UXSettingPriceDisplay,
          flag ? APP_PRICE_DISPLAY.ODDS : APP_PRICE_DISPLAY.TRADE_PRICES,
        );
      }
      commit('setGuestSettingPriceDisplayDirty', true);
      return Promise.resolve();
    },
    setSettingOrderbookCollapsed({ commit, rootGetters }, flag) {
      commit('setSettingOrderbookCollapsed', flag);
      if (rootGetters['user/isLoggedIn']) {
        return api.profile.putSettings(
          'ux',
          UX_CATEGORY.UXSettingIsOBCollapsedOnOS,
          flag ? APP_MOBILE_ORDERBOOK_DISPLAY.COLLAPSED : APP_MOBILE_ORDERBOOK_DISPLAY.EXPANDED,
        );
      }
      return Promise.resolve();
    },
    setSettingColorPalette({ commit, rootGetters }, value) {
      commit('setSettingColorPalette', value);
      if (rootGetters['user/isLoggedIn']) {
        commit('setGuestSettingColorPaletteDirty', false);
        return api.profile.putSettings('ux', UX_CATEGORY.UXSettingBuyColorScheme, value);
      }
      commit('setGuestSettingColorPaletteDirty', true);
      return Promise.resolve();
    },
    setSettingMobileLayout({ commit, rootGetters }, value) {
      commit('setSettingMobileLayout', value);
      if (rootGetters['user/isLoggedIn']) {
        commit('setGuestSettingMobileLayoutDirty', false);
        return api.profile.putSettings('ux', UX_CATEGORY.UXSettingMobileContractLayout, value);
      }
      commit('setGuestSettingMobileLayoutDirty', true);
      return Promise.resolve();
    },
    setSettingDirectionButtonVersion({ commit, rootGetters }, value) {
      commit('setSettingDirectionButtonVersion', value);
      if (rootGetters['user/isLoggedIn']) {
        commit('setGuestSettingDirectionButtonVersionDirty', false);
        return api.profile.putSettings('ux', UX_CATEGORY.UXSettingDirectionButtonVersion, value);
      }
      commit('setGuestSettingDirectionButtonVersionDirty', true);
      return Promise.resolve();
    },
    setSettingsAnnouncementCurState({ commit }, value) {
      return api.profile.putSettings('ux', UX_CATEGORY.UXSettingAnnouncementCurState, value);
    },
    applySettingShowOdds({ commit }, flag) {
      commit('setSettingShowOdds', flag);
    },
    applySettingOrderbookCollapsed({ commit }, flag) {
      commit('setSettingOrderbookCollapsed', flag);
    },
    applySettingColorPalette({ commit }, value) {
      commit('setSettingColorPalette', value);
    },
    applySettingMobileLayout({ commit }, value) {
      commit('setSettingMobileLayout', value);
    },
    applySettingDirectionButtonVersion({ commit }, value) {
      commit('setSettingDirectionButtonVersion', value);
    },
    setGuestSettingDirty({ commit }, { keyName, value }) {
      if (keyName === UX_CATEGORY.UXSettingBuyColorScheme) {
        commit('setGuestSettingColorPaletteDirty', value);
      } else if (keyName === UX_CATEGORY.UXSettingPriceDisplay) {
        commit('setGuestSettingPriceDisplayDirty', value);
      } else if (keyName === UX_CATEGORY.UXSettingMobileContractLayout) {
        commit('setGuestSettingMobileLayoutDirty', value);
      } else if (keyName === UX_CATEGORY.UXSettingDirectionButtonVersion) {
        commit('setGuestSettingDirectionButtonVersionDirty', value);
      } else if (keyName === UX_CATEGORY.UXSettingColorTheme) {
        commit('setGuestSettingColorThemeDirty', value);
      }
    },
    setPinboardModeVisible({ commit }, payload) {
      commit('setPinboardModeVisible', payload);
    },
    toggleSidebarVisible({ commit, state }) {
      commit('setSidebarVisible', !state.sidebarVisible);
    },
    setSettingTheme({ commit, dispatch, rootGetters }, value) {
      dispatch('applySettingTheme', value);
      if (rootGetters['user/isLoggedIn']) {
        commit('setGuestSettingColorThemeDirty', false);
        return api.profile.putSettings(
          'ux',
          UX_CATEGORY.UXSettingColorTheme,
          themeToSettingValue(normalizeTheme(value)),
        );
      }
      commit('setGuestSettingColorThemeDirty', true);
      return Promise.resolve();
    },
    applySettingTheme({ commit }, value) {
      const preference = normalizeTheme(value);
      commit('setThemePreference', preference);
      try {
        ls.set(themeStorageKey, preference);
      } catch {
        /* keep session preference */
      }
    },
  },
};
